import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
  Signal,
  signal,
} from '@angular/core';
import { IFingerprint } from '@core/interfaces/fingerprint.Interface';
import {
  AssistedPatientService,
  BeneficiaryService,
  BiometricService,
  ComponentUtilsService,
  FingerprintService,
} from '@core/services';
import { ClaimService } from '@core/services/http/claim.service';
import {
  biometricValidationMethod,
  buttonConfig,
  customComponentListener,
  labelType,
  radioConfig,
} from '@core/types/types';
import {
  bootstrapArrowClockwise,
  bootstrapCheck,
  bootstrapInfoCircle,
} from '@ng-icons/bootstrap-icons';
import { TranslateModule } from '@ngx-translate/core';
import { CustomButtonComponent } from '@shared/components/button/button.component';
import { IconsComponent } from '@shared/components/icons/icon.component';
import {
  radioOptionSelected,
} from '@shared/components/radio/radio.component';
import { Subscription } from 'rxjs';
import { biometricCaptureFingerprintSvgIcon } from 'src/assets/icons/icons';

const fingerprintCaptureStatuses = {
  'in-progress': 'in-progress',
  captured: 'captured',
  failed: 'failed',
  retrying: 'retrying',
  validating: 'validating',
  validated: 'validated',
  invalid: 'invalid',
};

const fingerprintCaptureStatusMotives = {
  timeout: 'timeout',
  error: 'error',
  'quality-threshold-not-fulfilled': 'quality-threshold-not-fulfilled',
};

type fingerprintCaptureStatus = keyof typeof fingerprintCaptureStatuses;

export type fingerType = 'left-index' | 'right-index';

export type onBiometricFingerprintCapturedEvent = {
  method: biometricValidationMethod;
  selectedFinger: fingerType;
  fingerprintCapture: string;
  biometricValidationId?: string;
};

@Component({
  imports: [
    TranslateModule,
    // CustomRadioComponent,
    CustomButtonComponent,
    IconsComponent,
  ],
  standalone: true,
  selector: 'biometric-capture-fingerprint-component',
  templateUrl: './capture-fingerprint.component.html',
  styleUrls: ['./capture-fingerprint.component.scss'],
})
export class CustomBiometricCaptureFingerPrintComponent {
  @Input({ required: false }) listeners!: customComponentListener[];

  private claimService = inject(ClaimService);
  private assistedPatientService = inject(AssistedPatientService);

  protected initialClaimInfo = this.claimService.initialClaimInfo;
  protected selectedCompanion = this.assistedPatientService.selectedCompanion;

  @Output() onBiometricFingerprintCaptured =
    new EventEmitter<onBiometricFingerprintCapturedEvent>();

  @Output() onBiometricFingerprintValidated =
    new EventEmitter<onBiometricFingerprintCapturedEvent>();

  private fingerprint$?: Subscription;
  private biometric$?: Subscription;

  protected iconCheck = bootstrapCheck;

  protected signals = {
    fingerprintCapture: {
      status: signal<fingerprintCaptureStatus | undefined>(undefined),
      statusMotive: signal<labelType | undefined>(undefined),
      value: signal<IFingerprint | undefined>(undefined),
    },
    finger: {
      selected: signal<fingerType | undefined>(undefined),
    },
  };

  constructor(
    private utils: ComponentUtilsService,
    private biometricService: BiometricService,
    private fingerprintService: FingerprintService,
    private beneficiaryService: BeneficiaryService
  ) {}

  private fingersId = {
    left: 'left-index',
    right: 'right-index',
  };

  protected radioOptions: Signal<radioConfig[]> = computed(() => [
    {
      id: this.fingersId.right,
      color: 'secondary',
      disabled:
        ['in-progress', 'validating', 'retrying'].includes(
          this.signals.fingerprintCapture.status()!
        ) ||
        (this.signals.finger.selected() != this.fingersId.right &&
          ['captured', 'validated'].includes(
            this.signals.fingerprintCapture.status()!
          )),
      label: {
        key: 'app.pageId.biometric.capture-fingerprint.fingers.right.index',
      },
    },
    {
      id: this.fingersId.left,
      color: 'secondary',
      disabled:
        ['in-progress', 'validating', 'retrying'].includes(
          this.signals.fingerprintCapture.status()!
        ) ||
        (this.signals.finger.selected() != this.fingersId.left &&
          ['captured', 'validated'].includes(
            this.signals.fingerprintCapture.status()!
          )),
      label: {
        key: 'app.pageId.biometric.capture-fingerprint.fingers.left.index',
      },
    },
  ]);

  protected btns = computed(() => ({
    capture: {
      type: 'button',
      label: {
        key: `app.pageId.biometric.common.${
          !(this.signals.fingerprintCapture.status() == 'in-progress')
            ? 'capture'
            : 'capturing'
        }`,
      },
      startContent: {
        content: IconsComponent,
        data: {
          icon: biometricCaptureFingerprintSvgIcon,
          size: 25,
          color: 'white',
        },
      },
      hidden:
        this.signals.fingerprintCapture.status() &&
        !this.signals.fingerprintCapture
          .status()
          ?.includes(fingerprintCaptureStatuses['in-progress']),
      disabled: this.signals.fingerprintCapture.status() == 'in-progress',
      action: this.captureFingerprint.bind(this),
    } as buttonConfig,
    retryCapture: {
      type: 'button',
      label: {
        key: `app.pageId.biometric.common.${
          !(this.signals.fingerprintCapture.status() == 'retrying')
            ? 'retry'
            : 'retrying'
        }`,
      },
      color: 'secondary',
      startContent: {
        content: IconsComponent,
        data: {
          icon: bootstrapArrowClockwise,
          size: 25,
          color: 'white',
        },
      },
      hidden:
        this.signals.fingerprintCapture.status() == undefined ||
        this.signals.fingerprintCapture
          .status()
          ?.includes(fingerprintCaptureStatuses['in-progress']) ||
        this.signals.fingerprintCapture
          .status()
          ?.includes(fingerprintCaptureStatuses.validating) ||
        this.signals.fingerprintCapture
          .status()
          ?.includes(fingerprintCaptureStatuses.captured),
      action: this.retryCaptureFingerprint.bind(this),
      disabled: this.signals.fingerprintCapture.status() == 'retrying',
    } as buttonConfig,
    validateCapture: {
      type: 'button',
      label: {
        key: `app.pageId.biometric.common.${
          this.signals.fingerprintCapture.status() == 'captured' ||
          this.signals.fingerprintCapture.status() == 'validated'
            ? 'validate'
            : 'validating'
        }`,
      },
      color: 'primary',
      startContent: {
        content: IconsComponent,
        data: {
          icon: bootstrapCheck,
          size: 25,
          color: 'white',
        },
      },
      hidden:
        this.signals.fingerprintCapture.value() == undefined ||
        this.signals.fingerprintCapture.status() == 'failed' ||
        this.signals.fingerprintCapture.status() == 'invalid',
      action: this.validateFingerprint.bind(this),
      disabled: this.signals.fingerprintCapture.status() == 'validating',
    } as buttonConfig,
    biometricIndications: {
        type: 'icon',
        icon: bootstrapInfoCircle,
        label: {
          key: 'app.pageId.biometric.messages.biometric-extra-info'
        },
        color: 'primary',
        size: 'md',
        theme: 'ghost',
    } as buttonConfig,
  }));

  ngOnInit() {
    this.utils.attachListeners(this, this.listeners);
  }

  onRadioOptionSelected = (event: radioOptionSelected) => {
    this.signals.finger.selected.set(event.option as fingerType);
  };

  captureFingerprint = () => {
    this.signals.fingerprintCapture.status.set('in-progress');
    this.subscribeToCaptureFingerprint();
  };

  retryCaptureFingerprint = () => {
    this.signals.fingerprintCapture.value.set(undefined);
    this.signals.fingerprintCapture.status.set('retrying');
    this.signals.fingerprintCapture.statusMotive.set(undefined);

    this.fingerprint$?.unsubscribe();

    this.onBiometricFingerprintCaptured.emit(undefined);

    this.subscribeToCaptureFingerprint();
  };

  subscribeToCaptureFingerprint = () => {
    this.fingerprint$ = this.fingerprintService
      .captureFingerprint()
      .subscribe((fingerprint: IFingerprint) => {
        this.signals.fingerprintCapture.value.set(fingerprint);

        if (!fingerprint.success) {
          this.signals.fingerprintCapture.status.set('failed');
          if (fingerprint.errorMessage?.toLowerCase().includes('timeout'))
            this.signals.fingerprintCapture.statusMotive.set({
              key: `${fingerprintCaptureStatusMotives.timeout}`,
            });
          else
            this.signals.fingerprintCapture.statusMotive.set({
              key: `${fingerprintCaptureStatusMotives.error}`,
            });
          return;
        }

        const qualityThreshold = this.fingerprintService.getQuality();

        if (parseInt(fingerprint.quality!) < qualityThreshold) {
          this.signals.fingerprintCapture.status.set('failed');
          this.signals.fingerprintCapture.statusMotive.set({
            key: `${fingerprintCaptureStatusMotives['quality-threshold-not-fulfilled']}`,
            params: { threshold: qualityThreshold },
          });
          return;
        }

        this.signals.fingerprintCapture.status.set('captured');

        this.onBiometricFingerprintCaptured.emit({
          method: 'fingerprint',
          selectedFinger: this.signals.finger.selected()!,
          fingerprintCapture: `data:image/jpeg;base64,${fingerprint.fingerprint!}`,
        });
      });
  };

  validateFingerprint = () => {
    const initialClaimInfo = this.claimService.initialClaimInfo()!;
    const selectedBeneficiary = this.beneficiaryService.selectedBeneficiary()!;

    this.signals.fingerprintCapture.status.set('validating');

    const validateBiometricPayload = this.selectedCompanion()
      ? {
          isPatientAssisted: true,
          identificationNumber: this.selectedCompanion()!.identificationNumber,
          biometricType: 'LECTURA_HUELLA',
          img: this.signals.fingerprintCapture.value()?.fingerprint!,
        }
      : {
          isPatientAssisted: false,
          beneficiaryId: selectedBeneficiary.id,
          providerSlug: initialClaimInfo.providerSlug,
          biometricType: 'LECTURA_HUELLA',
        img: this.signals.fingerprintCapture.value()?.fingerprint!,
        };
        
    this.biometric$ = this.biometricService
      .validateBiometric(
        validateBiometricPayload as any,
        initialClaimInfo.token!
      )
      .subscribe({
        next: (response) => {
          if (response.data.status == 'VERIFICATION_SUCCESS') {
            this.signals.fingerprintCapture.status.set('validated');
            this.onBiometricFingerprintValidated.emit({
              method: 'fingerprint',
              selectedFinger: this.signals.finger.selected()!,
              fingerprintCapture: `data:image/jpeg;base64,${this.signals.fingerprintCapture.value()!
                .fingerprint!}`,
              biometricValidationId: response.data.biometricValidationId,
            });
          } else {
            this.signals.fingerprintCapture.statusMotive.set({
              key: response.data.statusMessage,
            });
            this.signals.fingerprintCapture.status.set('invalid');
          }
        },
        error: (error) => {
          this.signals.fingerprintCapture.statusMotive.set({
            key: 'app.pageId.biometric.messages.error',
          });
          this.signals.fingerprintCapture.status.set('invalid');
        },
      });
  };

  ngOnDestroy() {
    this.fingerprint$?.unsubscribe();
    this.biometric$?.unsubscribe();
  }
}
